import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, catchError, filter, firstValueFrom, from, map, Observable, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { DecodedToken } from '../../../modules/auth/models/auth.model';
import { CognitoService } from '../../../modules/auth/services/cognito.service';
import { BaseResponseDetail, BaseResponseReport } from '../../interfaces/base-response/base-response.interface';
import { PublicKeyToken, RbacToken } from '../../interfaces/rbac/rbac.models';
import { RbacUser, UserCreation, UserUpdate } from '../../interfaces/rbac/user.interface';
import { fetchWithErrorHandling } from '../../utils/config/http.fetch.util';
import { BaseService } from '../base.service';
import { ConsorciaService } from '../consortium/consorcia.service';
import * as Forge from 'node-forge';

@Injectable({
  providedIn: 'root'
})
export class RbacService extends BaseService<any> {
  public cIdSubject = new BehaviorSubject<string | null>(null);
  public cId$ = this.cIdSubject.asObservable();
  private apiUrl = environment.apiUrl;
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);

  public get isAuthenticated$(): Observable<boolean> {
    return this.isAuthenticatedSubject.asObservable();
  }

  public setAuthenticated(isAuthenticated: boolean): void {
    this.isAuthenticatedSubject.next(isAuthenticated);
  }

  public user = new BehaviorSubject<DecodedToken>(null as any);
  public user$ = this.user.asObservable().pipe(filter((user) => user && !!user.permissions));

  constructor(
    private _http: HttpClient,
    private _cognitoService: CognitoService,
    private _consortiumService: ConsorciaService,
    private _router: Router
  ) {
    super(_http, 'rbac/tokens');
    if (localStorage.getItem('rbac-token') as string) {
      this.loadTokenPayload();
    }
  }

  public isAllowed(routePermissions: string[] = []): boolean{
    const user = this.user.getValue(); // Obtiene el usuario actual

    if (!user || !user.permissions) {
      return false;
    }

    return routePermissions.every((scope) =>
      user.permissions.some((permission) => permission.startsWith(scope))
    );
  }

  public async loadTokenPayload() {
    const sessionData = await this._cognitoService.getUserSession();
    const rbacToken = localStorage.getItem('rbac-token') as string;

    if (rbacToken) {
      const user: DecodedToken = jwtDecode(rbacToken);
      this.user.next(user);

      const [permissions, userDetails] = await Promise.all([
        firstValueFrom(this.getUserPermissions(user.UserApplicationId).pipe(
          map((response) => response.data)
        )),
        firstValueFrom(this.getUserById(user.nameidentifier).pipe(
          map((response) => response.data)
        )),
      ]);

      this.user.next({ ...user, permissions: permissions, details: userDetails });

    } else {
      await this.getRbacToken(sessionData?.tokens?.accessToken);
    }
  }

  public getRbacToken(cognitoToken: string): Promise<BaseResponseDetail<RbacToken>> | null {
    const headers = new HttpHeaders()
      .set('Authorization', `Bearer ${cognitoToken}`)
      .set('Application-Name', 'Solvex-RBAC');

    return firstValueFrom(
      this._http.get<BaseResponseDetail<RbacToken>>(`${this.baseUrl}/generates`, { headers }).pipe(
        tap((response: BaseResponseDetail<RbacToken>) => {
          localStorage.setItem('rbac-token', response.data.accessToken);
          this.setAuthenticated(true);
          localStorage.setItem('refresh_token', response.data.refreshToken);
          localStorage.setItem('last_refreshed_at', Date.now().toString());

          this.loadTokenPayload();
        })
      )
    );
  }

  public getPublicKeyToken(rbacToken: string): Promise<BaseResponseDetail<PublicKeyToken>> {
    const headers = new HttpHeaders()
      .set('Authorization', `Bearer ${rbacToken}`)
      .set('Application-Name', 'Solvex-RBAC');

    return firstValueFrom(
      this._http.get<BaseResponseDetail<PublicKeyToken>>(`${this.baseUrl}/key`, { headers })
    );
  }

  public getUsers(pageNumber: number, pageSize: number, criteria?: string): Observable<any> {
    const rbacToken = localStorage.getItem('rbac-token');

    let endpoint: string = `${this.apiUrl}/rbac/users?PageNumber=${pageNumber}&ItemsPerPage=${pageSize}`;

    if (criteria) {
      endpoint += `&Filter=name=${criteria}`;
    }

    const fetchPromise = fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${rbacToken}`,
        'Application-Name': 'Solvex-RBAC',
        'Tenant': '2a707b27-5cb4-4149-b800-b9e0c6566893'
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        return data;
      })
      .catch((error) => {
        throw error;
      });

    return from(fetchPromise);
  }

  public getUserSessions(filters: any): Observable<BaseResponseDetail<any>> {
    const rbacToken = localStorage.getItem('rbac-token');

    const cleanedFilter = Object.fromEntries(
      Object.entries({
        ...filters,
        cashStatus: filters.cashStatus?.length > 0 ? filters.cashStatus?.join(',') : undefined,
        groupId: filters.groupId?.length > 0 ? filters.groupId?.join(',') : undefined,
        bankId: filters.bankId?.length > 0 ? filters.bankId?.join(',') : undefined,
      }).filter(([_, value]) => value !== undefined && value !== '')
    );

    const queryString = new URLSearchParams(cleanedFilter as Record<string, string>).toString();
    const endpoint = `${this.apiUrl}/rbac/users/sessions${queryString ? '?' + queryString : ''}`;

    const fetchPromise = fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${rbacToken}`,
        'Application-Name': 'Solvex-RBAC',
        'Tenant': '2a707b27-5cb4-4149-b800-b9e0c6566893',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .catch((error) => {
        throw error;
      });

    return from(fetchPromise);
  }

  public getUserActivities(id: string) {
    let endpoint = `${this.apiUrl}/rbac/users/sessions/details?PageNumber=0&ItemsPerPage=1000&criteria=${id}`;

    return fetchWithErrorHandling<any>(endpoint, {
      method: 'GET',
      headers: {
        'Tenant': localStorage.getItem('cId') || '',
        'Authorization': `Bearer ${localStorage.getItem('rbac-token')}`
      }
    });
  }

  public createUser(user: UserCreation): Observable<any> {
    const rbacToken = localStorage.getItem('rbac-token');
    if (!rbacToken) {
      throw new Error('RBAC token is not available.');
    }

    const fetchObservable = from(
      this.getPublicKeyToken(rbacToken).then(response => {

        const xIdentity = response.data.identity;
        const publicKeyPem = response.data.publicKey;

        const publicKey = Forge.pki.publicKeyFromPem(publicKeyPem);
        const encryptedBytes = publicKey.encrypt(Forge.util.encodeUtf8(user.password), 'RSA-OAEP', {
          md: Forge.md.sha256.create()
        });

        user.password = Forge.util.encode64(encryptedBytes);

        const endpoint: string = `${this.apiUrl}/rbac/users`;

        return fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${rbacToken}`,
            'Application-Name': 'Solvex-RBAC',
            'x-identity': xIdentity || ''
          },
          body: JSON.stringify(user),
        })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          return data;
        });
      })
    );

    return fetchObservable;
  }

  public updateUser(user: UserUpdate, id: string): Observable<UserUpdate> {
    const rbacToken = localStorage.getItem('rbac-token');
    if (!rbacToken) {
      throw new Error('RBAC token is not available.');
    }

    const fetchObservable = from(
      this.getPublicKeyToken(rbacToken).then(response => {
        const xIdentity = response.data.identity;

        const endpoint: string = `${this.apiUrl}/rbac/users/${id}`;

        return fetch(endpoint, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${rbacToken}`,
            'Application-Name': 'Solvex-RBAC',
            'x-identity': xIdentity || ''
          },
          body: JSON.stringify(user),
        }).then(async (response) => {
          const data = await response.json();

          if (!response.ok) {
            throw { status: response.status, error: data };
          }

          return data as UserUpdate;
        });
      })
    );

    return fetchObservable;
  }

  public getUserById(userId: string): Observable<BaseResponseDetail<RbacUser>> {
    const rbacToken = localStorage.getItem('rbac-token');

    let endpoint: string = `${this.apiUrl}/rbac/users/${userId}`;

    const fetchPromise = fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${rbacToken}`,
        'Application-Name': 'Solvex-RBAC',
        'Tenant': localStorage.getItem('cId') || ''
      },
    })
      .then((response) => {
        if (!response.ok) {
          this._cognitoService.logout();
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        return data;
      })
      .catch((error) => {
        throw error;
      });

    return from(fetchPromise);
  }

  public getUserPermissions(userApplicationId: string): Observable<BaseResponseReport<string>> {
    let endpoint = `${this.apiUrl}/rbac/user-applications/${userApplicationId}/permissions`;

    return fetchWithErrorHandling<BaseResponseReport<string>>(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('rbac-token')}`
      }
    });
  }

  public changeUserPassword(userId: string, password: string): Observable<any> {
    const rbacToken = localStorage.getItem('rbac-token');
    if (!rbacToken) {
      throw new Error('RBAC token is not available.');
    }

    const fetchObservable = from(
      this.getPublicKeyToken(rbacToken).then(async (response) => {
        if (!response) {
          throw new Error('public key is not available.');
        }

        const publicKeyPem = response.data?.publicKey;
        const xIdentity = response.data?.identity;

        if (!publicKeyPem) {
          throw new Error('The public key is not available.');
        }

        // Encriptar la contraseña
        const publicKey = Forge.pki.publicKeyFromPem(publicKeyPem);
        const encryptedBytes = publicKey.encrypt(
          Forge.util.encodeUtf8(password),
          'RSA-OAEP',
          { md: Forge.md.sha256.create() }
        );
        const newPassword = Forge.util.encode64(encryptedBytes);

        const endpoint = `${this.apiUrl}/rbac/users/${userId}/change-password`;

        // Ejecutar el fetch
        const responseFetch = await fetch(endpoint, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${rbacToken}`,
            'x-identity': xIdentity || ''
          },
          body: JSON.stringify({ password: newPassword })
        });

        // Intentar parsear la respuesta como JSON
        const responseBody = await responseFetch.json().catch(() => ({}));

        if (!responseFetch.ok) {
          throw {
            status: responseFetch.status,
            message: responseBody?.errorMessage || 'Error desconocido'
          };
        }

        return responseBody; // ✅ Esto es lo que llega como `data` en tu `.subscribe`
      })
    ).pipe(
      catchError((error) => {
        console.error('Error en changeUserPassword:', error);
        return throwError(() => error); // Puedes capturarlo en el componente
      })
    );

    return fetchObservable;
  }


   public changeUserStatus(userId: string, status: number) {
    let endpoint = `${this.apiUrl}/rbac/users/${userId}/change-status`;

    return fetchWithErrorHandling(endpoint, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('rbac-token')}`,
      },
      body: JSON.stringify(
        {
          status
        }
      ),
    });
  }
}
